import React from 'react';

import {useDispatch} from 'react-redux';
import {withRouter, WithRouterProps} from 'react-router';
import {PopoverBody} from 'reactstrap';

import TabbedModal from 'app/components/general/TabbedModal';
import {GenerateDocumentContentAvailable} from 'app/general/structures/document-alerts/GenerateDocumentContentAvailable';
import {showNotification, showToastNotification} from 'app/toasts/slice';
import {appendPathToURL} from 'app/urls';
import {gt} from 'app/utils/i18n';
import {getLocation} from 'app/utils/urls';

import {
  BaseDocumentContentType,
  DetailPolicySerializedType,
  GenericRelatedSerializedType,
  ProcedureHashidSerializedType,
  ProcedureSerializedType,
  UserJobSerializedType,
} from 'sweet-types';

interface Props extends WithRouterProps {
  contentType: BaseDocumentContentType;
  version:
    | GenericRelatedSerializedType<'procedureversion'>
    | GenericRelatedSerializedType<'policyversion'>;
  document:
    | ProcedureSerializedType
    | ProcedureHashidSerializedType
    | DetailPolicySerializedType;
  onSuccess: () => any;
  onClose: () => void;
}

export const GenerateContentPopover = withRouter(
  ({
    version,
    document,
    onSuccess,
    onClose,
    router,
    location,
    contentType,
  }: Props): JSX.Element => {
    const dispatch = useDispatch();

    return (
      <TabbedModal
        show={true}
        onCancel={onClose}
        tabs={[
          {
            eventKey: 'delete-object',
            name: gt('Automatically Write Content'),
            icon: 'fa fa-robot',
            content: (
              <PopoverBody className="m-0 p-0">
                <GenerateDocumentContentAvailable
                  url={version.url}
                  initialPrompt={document.name}
                  contentType={contentType}
                  onGeneration={(promise: Promise<UserJobSerializedType>) =>
                    promise.then(
                      (jobResults) => {
                        onClose();
                        onSuccess();

                        if (jobResults.status === 'completed') {
                          const processHashid = location.query.gid;
                          let editUrl = 'edit/';

                          if (processHashid != null) {
                            editUrl += '?gid=' + processHashid;
                          }
                          router.replace(
                            getLocation(
                              appendPathToURL(jobResults.result.url!, editUrl)
                            )
                          );
                        }
                      },
                      (error) => {
                        onClose();
                        if (error.status === 429) {
                          dispatch(
                            showNotification(
                              gt(
                                'Daily limit for for AI requests exceeded.' +
                                  ' Contact %(email)s if you want to use this feature more.',
                                {
                                  email: (
                                    <a
                                      className="text-nowrap"
                                      href="mailto:support@sweetprocess.com"
                                    >
                                      <i className="far fa-envelope mr-1" />
                                      support@sweetprocess.com
                                    </a>
                                  ),
                                }
                              )
                            )
                          );
                        } else if (error.status === 400) {
                          onSuccess();
                          dispatch(
                            showToastNotification(
                              gt(
                                'This draft document is now old. Please make updates to the most recent version instead.'
                              ),
                              {
                                level: 'error',
                                timeout: 3000,
                                link: {
                                  text: gt('Edit most recent version'),
                                  to: document.html_url + 'edit/',
                                },
                              }
                            )
                          );
                        } else {
                          dispatch(
                            showNotification(
                              gt(
                                'Error occurred while generating %s contents.' +
                                  ' Try again later.',
                                contentType
                              )
                            )
                          );
                        }
                      }
                    )
                  }
                  generationAllowed={true}
                />
              </PopoverBody>
            ),
          },
        ]}
      />
    );
  }
);
